"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useVerticalOverflow = exports.useOverflow = void 0;

var _slicedToArray2 = _interopRequireDefault(require("@babel/runtime/helpers/slicedToArray"));

var _react = require("react");

var _isNil = _interopRequireDefault(require("lodash/isNil"));

var _sum = _interopRequireDefault(require("lodash/sum"));

var _debounce = _interopRequireDefault(require("lodash/debounce"));

/**
 * Hook that computes the width of a container component, and widths of each of
 * its children.
 *
 * Used when UI requires patterns like "+ 3 more" when the screen width is too
 * small.
 *
 * @public
 * @param containerRef - ref of the container
 * @param childrenRef - Array of refs for children inside the container
 * @returns isOverflow {boolean} - true if children overflows
 * @returns widths {number[]} - array of widths of children in pixels
 * @returns width {number} - parent's width
 * @returns ready {boolean} - true if the component has completed computing
 * width, useful to hide overflowed elements with `.invisible`
 */
var useOverflow = function useOverflow(containerRef, childrenRef) {
  var _useState = (0, _react.useState)([]),
      _useState2 = (0, _slicedToArray2["default"])(_useState, 2),
      widths = _useState2[0],
      setWidths = _useState2[1];

  var _useState3 = (0, _react.useState)(0),
      _useState4 = (0, _slicedToArray2["default"])(_useState3, 2),
      width = _useState4[0],
      setWidth = _useState4[1];

  var _useState5 = (0, _react.useState)(false),
      _useState6 = (0, _slicedToArray2["default"])(_useState5, 2),
      ready = _useState6[0],
      setReady = _useState6[1];

  var computeParentWidth = (0, _debounce["default"])(function () {
    var node = containerRef.current;

    if ((0, _isNil["default"])(node)) {
      setWidth(0);
      return;
    }

    setWidth(node.clientWidth);
  }, 300, {
    leading: true
  });

  var computeChildrenWidths = function computeChildrenWidths() {
    if (!(0, _isNil["default"])(childrenRef.current)) {
      setWidths(function (widths) {
        return childrenRef.current.map(function (node, index) {
          if ((0, _isNil["default"])(node.current)) {
            return widths[index];
          }

          return node.current.clientWidth;
        });
      });
    }
  };

  (0, _react.useEffect)(function () {
    // Re-compute widths of children when parent width is changed
    setReady(false);
    computeChildrenWidths();
    setReady(true);
  }, [width]);
  (0, _react.useEffect)(function () {
    // Compute parent width on mount
    computeParentWidth(); // Compute width of parent container when resize event happens

    window.addEventListener('resize', computeParentWidth);
    return function () {
      window.removeEventListener('resize', computeParentWidth);
    };
  }, []);
  var isOverflow = (0, _sum["default"])(widths) > width;
  return {
    isOverflow: isOverflow,
    widths: widths,
    width: width,
    ready: ready
  };
};

exports.useOverflow = useOverflow;

var useVerticalOverflow = function useVerticalOverflow(containerRef) {
  var _useState7 = (0, _react.useState)(false),
      _useState8 = (0, _slicedToArray2["default"])(_useState7, 2),
      isOverflow = _useState8[0],
      setOverflow = _useState8[1];

  var computeOverflow = (0, _react.useCallback)((0, _debounce["default"])(function () {
    var target = containerRef.current;

    if (target) {
      var clientHeight = target.clientHeight,
          scrollHeight = target.scrollHeight,
          scrollTop = target.scrollTop;

      if (scrollTop + clientHeight < scrollHeight) {
        setOverflow(true);
        return;
      }
    }

    setOverflow(false);
  }), [containerRef]);
  (0, _react.useEffect)(function () {
    var target = containerRef.current;

    if (target) {
      target.addEventListener('scroll', computeOverflow);
      window.addEventListener('resize', computeOverflow);
      computeOverflow();
    }

    return function () {
      if (target) {
        target.removeEventListener('scroll', computeOverflow);
        window.removeEventListener('resize', computeOverflow);
      }
    };
  }, [containerRef]);
  return {
    isOverflow: isOverflow
  };
};

exports.useVerticalOverflow = useVerticalOverflow;
//# sourceMappingURL=overflow.js.map